import React from "react";
import { Button, Badge } from "react-bootstrap";
import moment from "moment";

const ProjectResourceAllocationTab = ({
  // Data
  resources,
  resourcesLoading,
  teamMemberDetails,

  // Permissions
  hasProjectAdminRole,
  hasProjectManagerRole,

  // Handlers
  handleCreateResource,
  handleEditResource,
  handleDeleteResource,
  formatDate,
  getResourceStatusBadge,
}) => {
  const canEdit = hasProjectAdminRole() || hasProjectManagerRole();

  return (
    <div className="project-view-fade-in">
      <div className="resource-allocation-section">
        <div
          className="d-flex justify-content-between align-items-center mb-4"
          style={{ margin: "15px" }}
        >
          <h4 className="mb-0">Resource Allocation</h4>
          {canEdit && (
            <Button variant="primary" size="sm" onClick={handleCreateResource}>
              Add Resource
            </Button>
          )}
        </div>

        {resourcesLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading resources...</span>
            </div>
            <p className="mt-2 text-muted">Loading resources...</p>
          </div>
        ) : resources.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-users fa-3x text-muted mb-3"></i>
            <h5 className="text-muted">No Resources Allocated</h5>
            <p className="text-muted">
              No resources have been allocated to this project yet.
            </p>
            {canEdit && (
              <Button variant="primary" onClick={handleCreateResource}>
                Add First Resource
              </Button>
            )}
          </div>
        ) : (
          <div className="resource-table-container">
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead className="table-resource">
                  <tr>
                    <th>Member</th>
                    <th>Role</th>
                    <th>Billable</th>
                    <th>Allocation %</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className="table-resource">
                  {resources.map((resource) => {
                    // Find user details for this resource member
                    const userDetail = teamMemberDetails.find(
                      (user) =>
                        user.userId === parseInt(resource.resourceMember),
                    );

                    // Get display name - username if available, otherwise fallback to ID
                    const displayName = userDetail
                      ? userDetail.username
                      : resource.resourceMember;

                    return (
                      <tr key={resource.resourceId}>
                        <td>
                          <strong>{displayName}</strong>
                          {userDetail && userDetail.email && (
                            <div className="text-muted small">
                              {userDetail.email}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className="resource-role">
                            {resource.resourceRole}
                          </span>
                        </td>
                        <td>
                          <Badge
                            bg={
                              resource.resourceBillable === "Billable"
                                ? "success"
                                : "warning"
                            }
                            className="billable-badge"
                          >
                            {resource.resourceBillable}
                          </Badge>
                        </td>
                        <td>
                          <div className="allocation-percentage">
                            <small className="text-muted">
                              {resource.resourceAllocaPercentage}%
                            </small>
                          </div>
                        </td>
                        <td>{formatDate(resource.resourceStartDate)}</td>
                        <td>{formatDate(resource.resourceEndDate)}</td>
                        <td>
                          <Badge
                            className={getResourceStatusBadge(
                              resource.resourceStatus,
                            )}
                          >
                            {resource.resourceStatus}
                          </Badge>
                        </td>
                        <td>
                          <div className="btn-group" role="group">
                            {canEdit && (
                              <>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleEditResource(resource)}
                                  title="Edit Resource"
                                  className="me-2"
                                >
                                  Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() =>
                                    handleDeleteResource(resource.resourceId)
                                  }
                                  title="Delete Resource"
                                >
                                  Delete
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectResourceAllocationTab;
